// Global Instructions Rule Applied!
import React, { useState } from 'react'
import { Button, List, Tag, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faComments, faEnvelope, faNoteSticky } from '@fortawesome/free-solid-svg-icons'
import { ShadowRichHtml } from '../../../components/ShadowRichHtml.tsx'
import { text, dateTimeText, userEntryLabel } from '../lib/format.ts'

/** Rows shown before "Show all" expands the list. */
const VISIBLE_COUNT = 10

// Same type → icon mapping as the desktop InteractionsSection, with antd palette colours.
const TYPE_ICONS: Record<string, { icon: any; color: string }> = {
  meeting: { icon: faCalendarAlt, color: '#1890ff' },
  email: { icon: faEnvelope, color: '#fa8c16' },
  note: { icon: faNoteSticky, color: '#722ed1' }
}

const iconFor = (type: unknown): { icon: any; color: string } =>
  TYPE_ICONS[String(type || '').toLowerCase()] || { icon: faComments, color: 'rgba(0,0,0,0.45)' }

/**
 * Render an interaction description/notes blob. Email bodies arrive as rich HTML — those
 * go through ShadowRichHtml (DOMPurify + shadow DOM, so email CSS can't leak into the
 * app). Plain text keeps its line breaks.
 */
function RichText({ value }: { value: string }): React.JSX.Element {
  if (/<[a-z][\s\S]*>/i.test(value)) {
    return (
      <div style={{ overflowX: 'auto' }}>
        <ShadowRichHtml html={value} />
      </div>
    )
  }
  return <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
}

type TimelineItem =
  | { kind: 'single'; key: string; row: any }
  | {
      kind: 'group'
      key: string
      head: any
      latest: any
      members: any[]
      size: number
      lowValueCount: number
    }

/**
 * Fold rows sharing prog__thread_group_id (stamped by get_interactions_data for
 * emails of one subject thread) into a single group item at the latest member's
 * position, headed by prog__thread_latest_substantive_id. Everything else stays a
 * single row.
 */
function foldThreads(rows: any[]): TimelineItem[] {
  const groups = new Map<string, Extract<TimelineItem, { kind: 'group' }>>()
  const items: TimelineItem[] = []
  for (const row of rows) {
    const groupId = row?.prog__thread_group_id
    if (!groupId) {
      items.push({ kind: 'single', key: String(row.interactions__id), row })
      continue
    }
    const existing = groups.get(groupId)
    if (existing) {
      existing.members.push(row)
      if (String(row.interactions__id) === String(row.prog__thread_latest_substantive_id)) existing.head = row
      continue
    }
    const group = {
      kind: 'group' as const,
      key: `thread:${groupId}`,
      head: row,
      latest: row,
      members: [row],
      size: Number(row.prog__thread_size) || 1,
      lowValueCount: Number(row.prog__thread_low_value_count) || 0
    }
    groups.set(groupId, group)
    items.push(group)
  }
  return items
}

const isSummaryPending = (row: any): boolean =>
  row?.prog__summary_status === 'pending' || row?.prog__summary_status === 'processing'

/** Placeholder while the summarizer has not written the description yet. */
function PendingSummary({ row }: { row: any }): React.JSX.Element | null {
  if (!isSummaryPending(row)) return null
  return (
    <Typography.Text type="secondary" italic>
      Summary pending…
    </Typography.Text>
  )
}

/** One message inside an expanded thread. */
function MemberRow({ row }: { row: any }): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const description = String(row.interactions__description || '').trim()
  const notes = String(row.interactions__notes || '').trim()
  const owner = userEntryLabel(row.interactions__owner)
  const meta = [dateTimeText(row.interactions__datetime), owner || null].filter(Boolean).join('  ·  ')
  const lowValue = Boolean(row.prog__content_kind_is_low_value)
  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        if (notes) setOpen(!open)
      }}
      style={{
        borderTop: '1px solid rgba(0,0,0,0.06)',
        padding: '6px 0',
        fontSize: 13,
        opacity: lowValue ? 0.7 : 1,
        cursor: notes ? 'pointer' : 'default'
      }}
    >
      <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
        {meta}
        {lowValue && row.prog__content_kind_label ? (
          <Tag style={{ marginLeft: 6 }}>{row.prog__content_kind_label}</Tag>
        ) : null}
      </div>
      {description ? <RichText value={description} /> : <PendingSummary row={row} />}
      {open && notes ? (
        <div style={{ marginTop: 6 }}>
          <RichText value={notes} />
        </div>
      ) : null}
    </div>
  )
}

/**
 * Read-only "recent activity" rows (emails, meetings, notes) shared by the company and
 * deal detail screens. Rows are InteractionRecord-shaped; the service returns them
 * newest-first. Tapping a row expands participants, the description, and notes inline;
 * long histories are capped behind "Show all".
 */
export function InteractionList({ interactions }: { interactions: any[] }): React.JSX.Element {
  const [showAll, setShowAll] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (interactions.length === 0) return <Typography.Text type="secondary">No activity.</Typography.Text>

  // Email threads fold into one row (same rule as the desktop timeline): rows
  // sharing prog__thread_group_id become a single item headed by the latest
  // substantive message; tapping it lists the members.
  const items = foldThreads(interactions)
  const visible = showAll ? items : items.slice(0, VISIBLE_COUNT)

  return (
    <>
      <List
        size="small"
        dataSource={visible}
        rowKey={(item: TimelineItem) => item.key}
        renderItem={(item: TimelineItem) => {
          if (item.kind === 'group') {
            const head = item.head
            const { icon, color } = iconFor(head.interactions__type)
            const expanded = expandedId === item.key
            const meta = [dateTimeText(item.latest.interactions__datetime), `${item.size} messages`]
              .filter(Boolean)
              .join('  ·  ')
            const description = String(head.interactions__description || '').trim()
            return (
              <List.Item
                onClick={() => setExpandedId(expanded ? null : item.key)}
                style={{ cursor: 'pointer', paddingLeft: 4, paddingRight: 4, display: 'block' }}
              >
                <List.Item.Meta
                  avatar={<FontAwesomeIcon icon={icon} style={{ color, marginTop: 4, width: 16 }} />}
                  title={text(head.interactions__subject)}
                  description={meta}
                />
                <div style={{ marginTop: 4, marginLeft: 28, fontSize: 13 }}>
                  {description ? <RichText value={description} /> : <PendingSummary row={head} />}
                  {item.lowValueCount > 0 ? (
                    <Tag style={{ marginTop: 4 }}>{item.lowValueCount} low-value</Tag>
                  ) : null}
                </div>
                {expanded ? (
                  <div style={{ marginTop: 8, marginLeft: 28 }}>
                    {item.members.map((member: any) => (
                      <MemberRow key={String(member.interactions__id)} row={member} />
                    ))}
                  </div>
                ) : null}
              </List.Item>
            )
          }
          const it = item.row
          const { icon, color } = iconFor(it.interactions__type)
          const expanded = expandedId === item.key
          const owner = userEntryLabel(it.interactions__owner)
          const meta = [dateTimeText(it.interactions__datetime), owner || null].filter(Boolean).join('  ·  ')
          const participants = (Array.isArray(it.prog__interaction_participants) ? it.prog__interaction_participants : [])
            .map(userEntryLabel)
            .filter(Boolean)
          const description = String(it.interactions__description || '').trim()
          const notes = String(it.interactions__notes || '').trim()
          const pending = !description && isSummaryPending(it)
          const expandable = Boolean(participants.length || description || notes || pending)
          return (
            <List.Item
              onClick={expandable ? () => setExpandedId(expanded ? null : item.key) : undefined}
              style={{ cursor: expandable ? 'pointer' : 'default', paddingLeft: 4, paddingRight: 4, display: 'block' }}
            >
              <List.Item.Meta
                avatar={<FontAwesomeIcon icon={icon} style={{ color, marginTop: 4, width: 16 }} />}
                title={text(it.interactions__subject)}
                description={meta || undefined}
              />
              {expanded ? (
                <div style={{ marginTop: 8, marginLeft: 28, fontSize: 13 }}>
                  {participants.length ? (
                    <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: description || notes ? 6 : 0 }}>
                      With {participants.join(', ')}
                    </div>
                  ) : null}
                  {description ? <RichText value={description} /> : pending ? <PendingSummary row={it} /> : null}
                  {notes ? (
                    <div style={{ marginTop: description ? 6 : 0 }}>
                      <RichText value={notes} />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </List.Item>
          )
        }}
      />
      {!showAll && items.length > VISIBLE_COUNT ? (
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <Button size="small" onClick={() => setShowAll(true)}>
            Show all ({items.length})
          </Button>
        </div>
      ) : null}
    </>
  )
}
